import { Box, Chip, useTheme } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { FunctionComponent, useContext } from 'react';
import { ViewHeader } from '../../../core/components/ViewHeader/ViewHeader';
import { AppContext } from '../../../core/config';
import { useAppTranslate, useBusinessContext } from '../../../core/hooks';
import { Loader } from '../../../ui/components';
import { ItsmBusinessContextConfig } from '../../config';
import { useItsmMaintenanceWorks } from '../../hooks';
import { mapOnTableChangeEventToPaginationViewModelChange } from '../../../core/model';
import { useLocalizedDateFormat } from '../../../core/hooks/DateFormat/DateFormat';
import { UnorderedList } from '../../../ui/components/UnorderedList/UnorderedList';

export const ItsmMaintenanceWorks: FunctionComponent = () => {
  useBusinessContext(ItsmBusinessContextConfig);
  const translate = useAppTranslate();
  const { customerId } = useContext(AppContext);
  const { maintenanceWorks, isFetched, isFetching, total, pagination, setPagination } = useItsmMaintenanceWorks(customerId);
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const localizedDateFormat = useLocalizedDateFormat();
  const renderDate = (date?: string): string => date ? localizedDateFormat.format(new Date(date)) : '-';

  const options: MUIDataTableOptions = {
    serverSide: true,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    sortOrder: pagination.sortOrder,
    count: total,
    enableNestedDataAccess: '.',
    selectToolbarPlacement: 'none',
    download: false,
    print: false,
    rowHover: false,
    expandableRows: false,
    selectableRows: 'none',
    expandableRowsHeader: false,
    filter: false,
    search: false,
    viewColumns: false,
    onTableChange: (action, tableState) => {
      const paginationChange = mapOnTableChangeEventToPaginationViewModelChange(action, tableState);
      if (paginationChange !== null) {
        setPagination(paginationChange?.pagination);
      }
    }
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'itsmId',
      label: translate('maintenanceWorks.itsmId'),
    },
    {
      name: 'status',
      label: translate('maintenanceWorks.status'),
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <Chip
            label={maintenanceWorks && maintenanceWorks[dataIndex].status}
            sx={{ background: '#D7EDFF', color: primaryColor }}
          />;
        }
      }
    },
    {
      name: 'sozOrderNumbers',
      label: translate('maintenanceWorks.sozOrderNumber'),
      options: {
        customBodyRender: (sozOrderNumbers: number[]) => <UnorderedList values={ sozOrderNumbers } />
      }  
    },
    {
      name: 'serviceNames',
      label: translate('maintenanceWorks.serviceName'),
      options: {
        customBodyRender: (serviceNames: string[]) => <UnorderedList values={ serviceNames } />
      }
    },
    {
      name: 'startTime',
      label: translate('maintenanceWorks.startTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(maintenanceWorks?.[dataIndex].startTime);
        }
      }
    },
    {
      name: 'endTime',
      label: translate('maintenanceWorks.endTime'),
      options: {
        customBodyRenderLite: dataIndex => {
          return renderDate(maintenanceWorks?.[dataIndex].endTime);
        }
      }
    }
  ];

  return <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
    <ViewHeader title={translate('maintenanceWorks.title')} />
    { isFetching ? (<Loader loading={true} />) : null }
    { !isFetching && isFetched && maintenanceWorks
      ? <MUIDataTable
          title=''
          columns={ columns }
          data={ maintenanceWorks }
          options={ options }
        />
      : null
    }
  </Box>;
};